import Head from 'next/head'
import { memo } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ROUTES from '~/shared/constants/routes.const'
import { AppLanguage, OG_LOCALE_BY_LANGUAGE } from '~/types/locale'

import { useUrl } from '~/hooks/useUrl'

import { getSubdomain } from '~/utils/domain'
import useCleanBasePathFromAsPath from '~/utils/useCleanBasePathFromAsPath'

import { MetaImage, Route, Video } from './Metadata.types'

import { resolveHreflangLinks } from './hreflang.util'

import { OGP_IMAGE_DIMENSIONS } from './Metadata.constants'
import { getCloudfrontUrl } from './Metadata.util'

function useDefaultImage(
  image?: MetaImage,
  canonicalPath?: string,
): { url: string; width: number; height: number; alt?: string } | undefined {
  const { asPath, locale } = useRouter()
  // A/B variant pages (see canonicalPath in Metatags) are served under a route that
  // differs from their public one via a middleware rewrite, so asPath alone would
  // resolve the wrong image below — canonicalPath, when given, is the public route.
  const baseRoute = (canonicalPath ?? asPath).split('/').slice(0, 2).join('/') as Route
  const srcKey = image?.srcKey ?? getSrcKey((locale ?? AppLanguage.EN) as AppLanguage, baseRoute)
  if (!srcKey && !image) return
  return {
    url: image?.url ?? getCloudfrontUrl({ ...image, srcKey }),
    ...OGP_IMAGE_DIMENSIONS,
    ...(image?.alt ? { alt: image.alt } : undefined),
  }
}
function getSrcKey(locale: AppLanguage, route?: Route): string {
  switch (route) {
    case ROUTES.ENCYCLOPEDIA:
      return 'meta-images/og-images/encyclopedia_v3.png'
    case ROUTES.DRILL:
      return 'meta-images/og-images/math-essentials_v3.png'
    case ROUTES.EXCEL:
      return 'meta-images/excel.png'
    case ROUTES.GEOGEBRA:
      return 'meta-images/GeoGebra.png'
    case ROUTES.DIGITAL_TOOL:
      return 'meta-images/og-images/digital-tools_v3.png'
    case ROUTES.BOOTCAMP:
      return 'meta-images/og-images/vcc_v3.png'
    case ROUTES.TUTOR_ON_DEMAND_BLOG:
      return 'meta-images/meta-images/blog.png'
    case ROUTES.BOOKING:
    case ROUTES.TUTOR_ON_DEMAND:
      return 'meta-images/og-images/tutor-on-demand_v3.png'
    case ROUTES.GAMES:
      return 'meta-images/og-images/games_v3.png'
    case ROUTES.MATH_VAULT:
      return 'meta-images/og-images/math-vault_v3.png'
    case ROUTES.STUDY_TIPS:
      return 'meta-images/og-images/study-tips_v3.png'
    case ROUTES.EXAMS:
      return 'meta-images/eksamensoppgaver.png'
    case ROUTES.SEARCH_RESULT:
      return 'icons/search.png'
    case ROUTES.SETTINGS:
      return 'icons/settings-icon.png'
    case ROUTES.STATISTICS:
      return 'meta-images/og-images/statistics_v3.png'
    case ROUTES.MATH_TOPICS:
      return 'meta-images/og-images/math-topics_v3.png'
    case ROUTES.LEADERBOARD:
      return 'meta-images/og-images/leaderboard_v3.png'
    case ROUTES.HOME:
      if (locale === AppLanguage.NO) {
        return 'meta-images/og-images/landing-page_no_v2.png'
      } else {
        return 'meta-images/og-images/landing-page_en_v2.png'
      }
    case ROUTES.STUDENT_PANEL:
      return 'meta-images/og-images/student-panel_v3.png'
    case ROUTES.COOKIE_POLICY:
    case ROUTES.LOGIN:
    case ROUTES.MEDIA:
    case ROUTES.MEDIA_MEDIABANK:
    case ROUTES.MEDIA_NEWS:
    case ROUTES.MEDIA_PRESS_RELEASES:
    case ROUTES.OUR_TEAM:
    case ROUTES.PRIVACY:
    case ROUTES.REGISTER:
    case ROUTES.TERMS:
    case ROUTES.DASHBOARD:
      return 'meta-images/og-images/dashboard_v3.png'
    default:
      if (locale === AppLanguage.NO) {
        return 'meta-images/og-images/landing-page_no_v2.png'
      } else {
        return 'meta-images/og-images/landing-page_en_v2.png'
      }
  }
}

const Metatags = ({
  metaDescription,
  title,
  h1,
  otherLocales,
  image,
  contentType = 'website',
  keywords,
  noindex,
  video,
  ogTitle,
  canonicalPath,
  localePaths,
}: {
  metaDescription?: string
  title?: string
  h1?: string
  otherLocales?: AppLanguage[]
  image?: MetaImage
  video?: Video
  contentType?: 'website' | 'article' | 'video.other'
  keywords?: string
  noindex?: boolean
  ogTitle?: string
  canonicalPath?: string
  localePaths?: Partial<Record<AppLanguage, string>>
}): React.ReactElement => {
  const { t } = useTranslation('generics')
  const locale = useRouter().locale
  const ogTitleValue = ogTitle ?? h1 ?? title
  const metaImage = useDefaultImage(image, canonicalPath)
  // When a page is served under a URL that is not its canonical one (A/B variant
  // pages served via the middleware rewrite), it passes its public route here to
  // override AppHead's default canonical link (same `key`) and align og:url.
  const pageUrl = useUrl(canonicalPath)
  const ogLocale = locale ? OG_LOCALE_BY_LANGUAGE[locale as AppLanguage] : undefined
  const alternativeLocales = otherLocales ?? Object.values(AppLanguage).filter((htmlLocale) => htmlLocale !== locale)
  const cleanAsPath = useCleanBasePathFromAsPath()
  // hreflang cluster — emitted ONLY when a page declares its siblings: a per-locale
  // path map (blog/mediabank; encyclopedia/drill via sharedLanguageKey siblings) or
  // same-path otherLocales (encyclopedia/drill fallback). Marketing/static pages
  // pass neither → no cluster; single-locale pages → no cluster.
  const hreflangLinks = resolveHreflangLinks({
    subdomain: getSubdomain(),
    canonicalPath,
    cleanAsPath,
    locale,
    otherLocales,
    localePaths,
  })
  const isArticle = contentType === 'article'
  const fbAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID
  return (
    <Head>
      {fbAppId && <meta content={fbAppId} property="fb:app_id" />}
      {noindex && <meta content="noindex" name="robots" />}
      {canonicalPath !== undefined && <link href={pageUrl} key="canonical" rel="canonical" />}
      {title && <title>{title}</title>}

      {metaDescription && (
        <>
          <meta content={metaDescription} name="description" />
          <meta content={metaDescription} property="og:description" />
        </>
      )}
      {keywords && <meta content={keywords} name="keywords" />}
      <meta content={contentType} property="og:type" />
      {isArticle && <meta content={t('subjects.math')} property="article:section" />}
      {isArticle && keywords && <meta content={keywords} property="article:tag" />}
      {ogTitleValue && <meta content={ogTitleValue} property="og:title" />}
      <meta content={pageUrl} property="og:url" />

      {ogLocale && <meta content={ogLocale} property="og:locale" />}
      <meta content="House of Math" property="og:site_name" />

      {metaImage && (
        <>
          <meta content="summary_large_image" name="twitter:card" />
          {title && <meta content={title} name="twitter:title" />}
          {metaDescription && <meta content={metaDescription} name="twitter:description" />}
          <meta content={metaImage.url} name="twitter:image" />
        </>
      )}
      {alternativeLocales.map((otherLocale) => {
        return <meta content={OG_LOCALE_BY_LANGUAGE[otherLocale]} key={otherLocale} property="og:locale:alternate" />
      })}
      {hreflangLinks.map(({ hreflang, href }) => (
        <link href={href} hrefLang={hreflang} key={`alternate-${hreflang}`} rel="alternate" />
      ))}
      {metaImage && (
        <>
          <meta content={metaImage.url} property="og:image" />
          {!video?.alt && metaImage.alt && <meta content={metaImage.alt} property="og:image:alt" />}
          <meta content="image/png" property="og:image:type" />
          {metaImage.width && <meta content={`${metaImage.width}`} property="og:image:width" />}
          {metaImage.height && <meta content={`${metaImage.height}`} property="og:image:height" />}
        </>
      )}
      {video && (
        <>
          <meta content={video.url} property="og:video" />
          <meta content={video.url} property="og:video:secure_url" />
          <meta content={video.urlType} property="og:video:type" />
          {video.alt && <meta content={video.alt} property="og:image:alt" />}
          {video.width && <meta content={`${video.width}`} property="og:video:width" />}
          {video.height && <meta content={`${video.height}`} property="og:video:height" />}
        </>
      )}
    </Head>
  )
}

export default memo(Metatags)
